/**
 * -----------------------------------------------------------------------------
 * User Detail Page
 * -----------------------------------------------------------------------------
 * This page is used to show the details of a single user of the organization.
 */

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDebouncedCallback } from "use-debounce";
import {
  useDeleteSingleUserMutation,
  useEditOrganizationUserMutation,
  useOrganizationUsersQuery,
  useSingleOrganizationUserQuery,
  useEditOrganizationUserACLMutation,
} from "../../../api/network/adminApiServices";
import { RESPONSE_CODE } from "../../../api/network/constants";
import { serializeErrorKeyValues } from "../../../api/network/errorCodes";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
  setUserData,
} from "../../../api/store/commonSlice";
import { LegacyEditOrganizationUserPayload } from "../../../api/types/Admin";
import { LegacyVerifyEmailOtpResponseSuccess } from "../../../api/types/Onboarding";
import { TLegacyUser, TLegacyUserWithAccount } from "../../../api/types/User";
import {
  TEditUserACLPayload,
  TLegacyUserRoleACL,
} from "../../../api/types/UserRole";
import svgIcons from "../../../assets/svg/svgIcons";
import Accordian from "../../../components/accordian";
import {
  AdminFormFieldSubmit,
  TSelectboxOption,
} from "../../../components/admin/formFields";
import HeaderView from "../../../components/admin/headerView";
import ModalDeleteConfirmation from "../../../components/admin/modalDeleteConfirmation";
import AdminListingColumnItem from "../../../components/adminListingColumnItem";
import FillAllRequiredFieldsCaption from "../../../components/fillAllRequiredFieldsCaption";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import AppSearchBox from "../../../components/searchBox";
import { APP_CONFIG } from "../../../constants/constants";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData, userDisplayName } from "../../../utils/user";
import { useReportUtils } from "../../reports/hooks/reportUtils";
import { UserFt1AccessField } from "../ft1Access";
import { ROLE_ACCESS_LEVELS } from "../role/constants";
import { FIRST_LOGIN_PAGE_OPTIONS } from "./constants";
import {
  UserAccessControlForm,
  UserAuthorizedGroupsForm,
  UserGeneralDetailForm,
} from "./user-form";
import {
  TFormFieldNames,
  userDetailsInitialValues,
  userDetailsYupValidationSchema,
} from "./validation";
import FormActionButtons, {
  FormActionButton,
} from "../../../components/admin/formActionButtons";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { UserAccessControlAclForm } from "./user-access-control-form";
const ScreenAdminDetailUser = () => {
  const { userId } = useParams<{ userId: string }>();
  const { state: locationState } = useLocation(); // OrganizationEntityListingPayload | { new: true }
  const { t: tMain } = useTranslation();
  const { t: tAdmin } = useTranslation("translation", {
    keyPrefix: "admins.users",
  });
  const { t } = useTranslation("translation", {
    keyPrefix: "admins.users.detailsPage",
  });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { refetchCustomReportListToStore } = useReportUtils();
  const { getUserACLAccessLevel } = useACL();

  const thisUserTimezone = useLoggedInUserData("timezone");

  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { users: orgUsersQueryParams } = listingQueryParams;

  const { getLocalStorageItem, setLocalStorageItem } = useLocalStorage();
  const loggedInUserData = useSelector(
    (state: { commonReducer: { user: LegacyVerifyEmailOtpResponseSuccess } }) =>
      state.commonReducer.user
  );
  const loggedInUser: TLegacyUser =
    loggedInUserData?.user ?? getLocalStorageItem(localStorageKeys.user);
  const sameAsLoggedInUser = loggedInUser?.userid === userId;
  const applicableACL = sameAsLoggedInUser
    ? ACL_NAMES.ACL_ADMIN_USER
    : ACL_NAMES.ACL_ADMIN_USER_ALL_USERS;

  // flag to idenfiy if user is coming from create new user popup
  const isNewEntity = React.useRef<boolean>(!!locationState?.new);

  // flag to enable edit mode
  const [userCanEdit, setUserCanEdit] = useState<boolean>(
    !!isNewEntity?.current
  );
  const [showUserAcl, setShowUserAcl] = useState<boolean>(false);

  // prepare query params for fetching organization users
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        users: { ...orgUsersQueryParams, page: 1, search: value },
      })
    );
  }, 500);

  // fetch organization users
  const {
    data: dataOrgUsers,
    isFetching: isFetchingOrgUsers,
    error,
  } = useOrganizationUsersQuery(orgUsersQueryParams);

  // fetch single user details
  const {
    data: dataSingleUser,
    isFetching: isFetchingSingleUser,
    isError: isErrorSingleEntity,
    error: errorSingleEntity,
  } = useSingleOrganizationUserQuery(
    {
      account_id: `${orgUsersQueryParams?.accountid}`,
      user_id: `${userId}`,
    },
    {
      skip: !userId,
      refetchOnMountOrArgChange: true, // prevent cached values from displaying when creating entity with same id
    }
  );
  useEffect(() => {
    if (isErrorSingleEntity) {
      if (
        (errorSingleEntity as FetchBaseQueryError)?.status ===
          RESPONSE_CODE.NOT_FOUND ||
        (errorSingleEntity as FetchBaseQueryError)?.status ===
          RESPONSE_CODE.BAD_REQUEST
      ) {
        toast.error(t("toast.user_not_found"));
        navigate(routeUrls.dashboardChildren.adminChildren.users);
      }
    }
  }, [isErrorSingleEntity, errorSingleEntity]);

  // user mutations
  const [editOrganizationUserApiTrigger, { isLoading: isLoadingEditUser }] =
    useEditOrganizationUserMutation();
  const [deleteSingleUserApiTrigger, { isLoading: isLoadingDeleteUser }] =
    useDeleteSingleUserMutation();
  const { results, count } = dataOrgUsers || {};

  const [editUserACLTrigger, { isLoading: isLoadingEditUserACL }] =
    useEditOrganizationUserACLMutation();
  // formik
  const [formikValuesReady, setFormikValuesReady] = useState<boolean>(false);
  useEffect(() => {
    setFormikValuesReady(!isFetchingSingleUser);
  }, [isFetchingSingleUser]);

  const formik = useFormik({
    initialValues: userDetailsInitialValues,
    validationSchema: userDetailsYupValidationSchema,
    onSubmit: (values) => {
      // prepare payload
      const { account, groups, ...singleUser } =
        dataSingleUser as TLegacyUserWithAccount;
      const preparedUserEditParam: LegacyEditOrganizationUserPayload = {
        user: {
          ...(singleUser as TLegacyUser),
          ...(!!values.password ? { password: values.password } : {}),
          contactemail: values.contact_email,
          contactname: values.contact_name,
          contactphone: values.contact_phone_number,
          defaultoverlay: values.default_overlay, // TODO: need to check
          mapDefault: parseInt(values.mapDefault) || 0,
          description: values.user_description,
          displayname: values.contact_name, // TOOD: check
          enablegeozonelabels: values.use_geozone_labels ? 1 : 0,
          firstloginpageid: values.first_login_page,
          isactive: values.is_active ? 1 : 0,
          ...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ROLE) >=
          ROLE_ACCESS_LEVELS.WRITE
            ? {
                roleid: values.default_acl_role,
              }
            : {}),
          maxaccesslevel: values.maximum_access_level, // TODO: need to implement
          // pinnedgroups: null, // TODO: need to implement
          requiretwofactor: values.use_two_factor ? 1 : 0,
          sessionexpire: values.session_timeout, // TODO: check this or tomcattimeout
          // snowdashtiles: string | null, // TODO: need to implement
          timezone: values.timezone,
          tomcattimeout: Math.floor((values.session_timeout || 0) / 60), // convert seconds to minutes (for BE)
          // user_state: values.user_state, // TODO: need to implement
          // usesso: values.enable_sso_to_visatracks ? 1 : 0, // disabled per client feedback
          ...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_GROUPS) >=
          ROLE_ACCESS_LEVELS.WRITE
            ? {
                group_ids: values.selected_groups.map((item) =>
                  typeof item === "object" && item !== null
                    ? (item as { value: string }).value
                    : item
                ),
              }
            : // {
              //     group_ids: values.selected_groups.map((item) => item.value),
              //   }
              {}),
        } as TLegacyUser,
      };

      // 2. Prepare payload for User ACLs
      const preparedUserACLParam: TEditUserACLPayload = {
        role_id: values.default_acl_role,
        user_id: userId || values.user_id,
        acls: (values.user_acls || []).map((acl: TLegacyUserRoleACL) => ({
          acl_id: acl.aclid,
          access_level: acl.accesslevel,
          description: acl.description || "",
        })),
      };

      // Fire both APIs concurrently
      const updateUserPromise = editOrganizationUserApiTrigger(
        preparedUserEditParam
      ).unwrap();

      // Only trigger ACL update if the form was shown and there's ACL data
      const updateUserACLPromise =
        showUserAcl && preparedUserACLParam.acls.length > 0
          ? editUserACLTrigger(preparedUserACLParam).unwrap()
          : Promise.resolve("ACLs not updated");

      Promise.all([updateUserPromise, updateUserACLPromise])
        .then(([userData, aclData]) => {
          // Both succeeded
          toast.success(t("toast.user_updated"));
          if (sameAsLoggedInUser) {
            // update user data in session storage
            const localStorageUserData = getLocalStorageItem(
              localStorageKeys.user
            );
            setLocalStorageItem(localStorageKeys.user, {
              ...localStorageUserData,
              contactname: values.contact_name,
              contactphone: values.contact_phone_number,
              displayname: values.contact_name,
              mapDefault: parseInt(values.mapDefault) || 0,
            });
            const localStorageLoggedInUserData = getLocalStorageItem(
              localStorageKeys.loggedInUserData
            );
            setLocalStorageItem(localStorageKeys.loggedInUserData, {
              ...localStorageLoggedInUserData,
              user: {
                ...localStorageLoggedInUserData.user,
                contactname: values.contact_name,
                contactphone: values.contact_phone_number,
                displayname: values.contact_name,
                mapDefault: parseInt(values.mapDefault) || 0,
              },
            });
            // update user data in redux store
            dispatch(
              setUserData({
                ...loggedInUserData,
                user: {
                  ...loggedInUserData.user,
                  contactname: values.contact_name,
                  contactphone: values.contact_phone_number,
                  displayname: values.contact_name,
                  mapDefault: parseInt(values.mapDefault) || 0,
                },
              })
            );
          }
          navigate(routeUrls.dashboardChildren.adminChildren.users);
        })
        .catch((error) => {
          const errors = !!error?.data
            ? serializeErrorKeyValues(error?.data)
            : [t("toast.updation_failed")];
          if (errors?.join(" ").includes("Email already in use")) {
            // don't show this toast error as it is being handled at redux toolkit level (showing here is causing duplicate error messages)
          } else {
            toast.error(errors?.join(" "));
          }
        });
    },
  });

  // pre-fill formik values
  useEffect(() => {
    if (dataSingleUser) {
      setFormikValuesReady(false); // simulate render delay for select pre-selected values
      formik.setValues({
        user_id: dataSingleUser?.userid,
        password: "",
        user_description: dataSingleUser?.description || "",
        is_active: dataSingleUser?.isactive === 1,
        use_two_factor: dataSingleUser?.requiretwofactor === 1,
        use_geozone_labels: dataSingleUser?.enablegeozonelabels === 1,
        contact_name: dataSingleUser?.contactname || "",
        contact_phone_number: dataSingleUser?.contactphone || "",
        contact_email: dataSingleUser?.contactemail || "",
        timezone: isNewEntity?.current
          ? (thisUserTimezone ?? "")
          : dataSingleUser?.timezone || "",
        // enable_sso_to_visatracks: dataSingleUser?.usesso === 1, // disabled per client feedback
        default_overlay: dataSingleUser?.defaultoverlay || "",
        mapDefault: `${dataSingleUser?.mapDefault || 0}`,
        user_state: "", // TODO: fix user state
        session_timeout: (dataSingleUser?.tomcattimeout || 0) * 60, // convert (BE) minutes to seconds for session timeout
        first_login_page: isNewEntity?.current
          ? FIRST_LOGIN_PAGE_OPTIONS[0].value
          : dataSingleUser?.firstloginpageid || "",
        // selected_groups: !!dataSingleUser?.groups
        //   ? dataSingleUser?.groups?.map((item) => item.groupid)
        //   : [],
        selected_groups:
          dataSingleUser?.groups?.map((item) => ({
            label: `${item.groupid}`,
            value: `${item.groupid}`,
          })) || [],
        all_groups:
          !dataSingleUser?.groups || dataSingleUser?.groups?.length === 0,
        default_acl_role: dataSingleUser?.roleid,
        maximum_access_level: dataSingleUser?.maxaccesslevel || 0,
        user_acls: [],
      });
      setUserCanEdit(!!isNewEntity?.current);
      setTimeout(() => {
        setFormikValuesReady(true);
      }, 200); // simulate render delay for select pre-selected values
    }
  }, [dataSingleUser, userId]);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    dirty,
  } = formik;

  const invalidFields = Object.keys(errors).filter(
    (key) => !!errors[key as TFormFieldNames]
  );
  const isFormValid = invalidFields.length === 0 && dirty;

  // handle edit user
  const handleEditUser = () => {
    if (userCanEdit) {
      formik.handleSubmit();
    }
  };

  // handle delete user
  const handleDeleteUser = () => {
    deleteSingleUserApiTrigger({ email: dataSingleUser?.contactemail ?? "" })
      .unwrap()
      .then(() => {
        toast.success(t("toast.user_deleted"));

        // re-fetch the reports and update the store
        refetchCustomReportListToStore();

        navigate(routeUrls.dashboardChildren.adminChildren.users);
      })
      .catch((error) => {
        const errors = !!error?.data
          ? serializeErrorKeyValues(error?.data)
          : [t("toast.deletion_failed")];
        toast.error(errors?.join(" "));
      });
  };

  const onBackButtonClick = () => {
    navigate(routeUrls.dashboardChildren.adminChildren.users);
  };
  const MemoizedFormActionButtons = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "danger",
              label: tMain("delete"),
              onClick: () => {
                dispatch(
                  setModalsData({
                    ...modalsState,
                    showDeleteConfirmation: true,
                  })
                );
              },
              disabled:
                isFetchingSingleUser ||
                isLoadingEditUser ||
                isLoadingEditUserACL ||
                getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) <
                  ROLE_ACCESS_LEVELS.ALL ||
                sameAsLoggedInUser, // prevent deleting self
            },
            ...(userCanEdit
              ? [
                  {
                    customWrapperClass: "w-24",
                    type: "button",
                    variant: "secondary",
                    label: tMain("cancel"),
                    onClick: () => onBackButtonClick(),
                  } as FormActionButton,
                ]
              : []),
            {
              customWrapperClass: "w-24",
              type: "button",
              variant: "primary",
              label: userCanEdit ? tMain("update") : tMain("edit"),
              onClick: userCanEdit
                ? handleEditUser
                : () => setUserCanEdit(!userCanEdit),
              disabled:
                isFetchingSingleUser ||
                isLoadingEditUser ||
                isLoadingEditUserACL ||
                (userCanEdit && !!Object.keys(errors).length) ||
                getUserACLAccessLevel(applicableACL) < ROLE_ACCESS_LEVELS.WRITE,
            },
          ]}
        />
      </>
    );
  }, [
    modalsState,
    isFetchingSingleUser,
    isLoadingEditUser,
    isLoadingEditUserACL,
    userCanEdit,
    errors,
  ]);
  const MemoizedFormActionSaveButton = useMemo(() => {
    return (
      <>
        <FormActionButtons
          buttons={[
            {
              type: "submit",
              variant: "success",
              label: tMain("save"),
              onClick: () => handleEditUser(),
              disabled:
                isLoadingEditUser ||
                isLoadingEditUserACL ||
                !!Object.keys(errors).length,
            },
          ]}
        />
      </>
    );
  }, [isLoadingEditUser, isLoadingEditUserACL, errors]);
  return (
    <>
      <HeaderView
        title={t("heading")}
        showBackButton={true}
        backButtonCallback={onBackButtonClick}
      />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} py-2`}>
        <div className="flex items-center">
          <p className="font-semibold text-accent-blue-dark text-lg leading-6">
            {t("sub_heading")}
          </p>
        </div>
        <div className="lg:grid lg:grid-cols-12 mt-8 py-2 gap-4">
          <div
            className={`hidden xl:block lg:col-span-3 space-y-4${isFetchingOrgUsers ? " opacity-40 pointer-events-none" : ""}${isNewEntity?.current ? " xl:hidden" : ""}`}
          >
            <div className="font-bold text-lg leading-6 mt-2 mb-3">
              {t("listing_heading")}
            </div>
            <AppSearchBox
              placeholder={tAdmin("search_placeholder")}
              initialValue={orgUsersQueryParams.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                debouncedSetSearchKeyword(e.target.value)
              }
              onClear={() => {
                debouncedSetSearchKeyword("");
              }}
            />
            <div className="max-h-admins overflow-y-auto">
              {dataOrgUsers?.results?.map(
                (item: TLegacyUserWithAccount, index: number) => (
                  <AdminListingColumnItem
                    key={index}
                    selected={userId === item.userid}
                    onClick={() =>
                      navigate(
                        `${routeUrls.dashboardChildren.adminChildren.users}/${item.userid}`
                      )
                    }
                    title={userDisplayName(item)}
                    description={item.description}
                    asideText={item.roleid}
                    bottomText={item.contactemail}
                  />
                )
              )}
            </div>
            {!isFetchingOrgUsers && (
              <Pagination
                pageSize={orgUsersQueryParams.page_size}
                handlePageSizeChange={(e: TSelectboxOption | null) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      users: {
                        ...orgUsersQueryParams,
                        page: 1,
                        page_size: parseInt(`${e?.value}`),
                      },
                    })
                  );
                }}
                totalPages={
                  count ? Math.ceil(count / orgUsersQueryParams.page_size) : 1
                }
                forcePage={orgUsersQueryParams.page - 1}
                handlePageClick={(data: TPaginationSelected) => {
                  dispatch(
                    setListingQueryParams({
                      ...listingQueryParams,
                      users: {
                        ...orgUsersQueryParams,
                        page: data?.selected + 1,
                      },
                    })
                  );
                }}
                onlyPageChange={true}
                size={1}
              />
            )}
          </div>
          <div
            className={`${isNewEntity?.current ? "lg:col-span-12" : "col-span-12 xl:col-span-9"}`}
          >
            <div className="flex items-center gap-4">
              {isNewEntity?.current ? (
                <>
                  <p className="font-semibold text-accent-blue-dark text-base leading-6">
                    {tMain("admins.completeCreation")}
                  </p>
                  <div className="flex-grow"></div>
                  {isFetchingSingleUser && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  <div className="w-24">
                    {/* <AdminFormFieldSubmit
                      type="submit"
                      variant="success"
                      label={tMain("save")}
                      onClick={handleEditUser}
                      disabled={
                        isLoadingEditUser || !!Object.keys(errors).length
                      }
                    /> */}
                    {MemoizedFormActionSaveButton}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex-grow"></div>
                  {isFetchingSingleUser && (
                    <img
                      src={svgIcons.LoadingAnimation}
                      alt="loading-animation"
                      className="w-8 h-8"
                    />
                  )}
                  {userCanEdit && !!Object.keys(errors).length && (
                    <FillAllRequiredFieldsCaption />
                  )}
                  {/* <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="danger"
                      label={tMain("delete")}
                      onClick={() => {
                        dispatch(
                          setModalsData({
                            ...modalsState,
                            showDeleteConfirmation: true,
                          })
                        );
                      }}
                      disabled={
                        isFetchingSingleUser ||
                        isLoadingEditUser ||
                        getUserACLAccessLevel(
                          ACL_NAMES.ACL_ADMIN_USER_ALL_USERS
                        ) < ROLE_ACCESS_LEVELS.ALL ||
                        sameAsLoggedInUser // prevent deleting self
                      }
                    />
                  </div>
                  {userCanEdit && (
                    <div className="w-24">
                      <AdminFormFieldSubmit
                        type="button"
                        variant="secondary"
                        label={tMain("cancel")}
                        onClick={onBackButtonClick}
                      />
                    </div>
                  )}
                  <div className="w-24">
                    <AdminFormFieldSubmit
                      type="button"
                      variant="primary"
                      label={userCanEdit ? tMain("update") : tMain("edit")}
                      onClick={
                        userCanEdit
                          ? handleEditUser
                          : () => setUserCanEdit(!userCanEdit)
                      }
                      disabled={
                        isFetchingSingleUser ||
                        isLoadingEditUser ||
                        (userCanEdit && !!Object.keys(errors).length) ||
                        getUserACLAccessLevel(applicableACL) <
                          ROLE_ACCESS_LEVELS.WRITE
                      }
                    />
                  </div> */}
                  {MemoizedFormActionButtons}
                </>
              )}
            </div>
            <div
              className={`rounded-lg mt-2 bg-accent-blue-pale transition ${isFetchingSingleUser || isLoadingEditUser || isLoadingDeleteUser || isLoadingEditUserACL ? "opacity-40" : ""}`}
            >
              <form onSubmit={handleSubmit}>
                <Accordian
                  title={t("accord_general_details")}
                  openByDefault={true}
                >
                  <UserGeneralDetailForm
                    values={values}
                    errors={errors}
                    touched={touched}
                    handleChange={handleChange}
                    formikSetValue={formik.setFieldValue}
                    handleBlur={handleBlur}
                    formikSetTouched={formik.setFieldTouched}
                    userCanEdit={userCanEdit}
                    loadingData={isFetchingSingleUser || !formikValuesReady}
                  />
                </Accordian>
                {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_GROUPS) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  <Accordian title={t("accord_authorized_groups")}>
                    <UserAuthorizedGroupsForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      formikSetValue={formik.setFieldValue}
                      handleBlur={handleBlur}
                      formikSetTouched={formik.setFieldTouched}
                      userCanEdit={
                        getUserACLAccessLevel(
                          ACL_NAMES.ACL_ADMIN_USER_GROUPS
                        ) >= ROLE_ACCESS_LEVELS.WRITE && userCanEdit
                      }
                      loadingData={isFetchingSingleUser || !formikValuesReady}
                    />
                  </Accordian>
                )}
                {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ROLE) >=
                  ROLE_ACCESS_LEVELS.READ && (
                  <Accordian title={t("accord_user_access_control")}>
                    <UserAccessControlForm
                      values={values}
                      errors={errors}
                      touched={touched}
                      handleChange={handleChange}
                      formikSetValue={formik.setFieldValue}
                      handleBlur={handleBlur}
                      formikSetTouched={formik.setFieldTouched}
                      userCanEdit={
                        getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ROLE) >=
                          ROLE_ACCESS_LEVELS.WRITE && userCanEdit
                      }
                      loadingData={isFetchingSingleUser || !formikValuesReady}
                      showUserAcl={showUserAcl}
                      setShowUserAcl={setShowUserAcl}
                    />
                    {showUserAcl && (
                      <UserAccessControlAclForm
                        values={values}
                        errors={errors}
                        touched={touched}
                        handleChange={handleChange}
                        formikSetValue={formik.setFieldValue}
                        handleBlur={handleBlur}
                        formikSetTouched={formik.setFieldTouched}
                        userCanEdit={userCanEdit}
                        loadingData={isFetchingSingleUser || !formikValuesReady}
                      />
                    )}
                  </Accordian>
                )}
              </form>
              {getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_USER_ALL_USERS) >=
                ROLE_ACCESS_LEVELS.ALL &&
                !!userId && (
                  <Accordian title={tMain("ft1_access.accord_title")}>
                    <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
                      <UserFt1AccessField
                        accountId={`${orgUsersQueryParams?.accountid}`}
                        userId={userId}
                        userCanEdit={userCanEdit}
                      />
                    </div>
                  </Accordian>
                )}
            </div>
          </div>
        </div>
      </div>
      <ModalDeleteConfirmation handleDeleteAdmin={handleDeleteUser} />
    </>
  );
};

export default ScreenAdminDetailUser;
